/**
 * Shared trigger store for the single {@link RecordingSetupPrompt}, opened by the
 * encryption orchestrator ({@link ensureDeviceEncryption}) and the storage settings UI.
 *
 * @remarks
 * Auto-open is guarded to once per device per session: the orchestrator marks a device
 * and skips re-opening while {@link hasPrompted} is true. The guard is in-memory only, so
 * it clears on reload and is reset on logout (see `clearCameraCredentials`); the persistent
 * Setup CTA still opens the prompt on demand afterwards.
 */

import { get, writable } from 'svelte/store';

import type { Device } from 'src/types/Device';
import { real_id } from 'src/helpers/interface_helpers';

/** State of the shared recording-setup prompt. */
export interface CameraSetupPromptState {
	deviceId: string;
	open: boolean;
	promptedDevices: string[];
}

const initialState: CameraSetupPromptState = { deviceId: '', open: false, promptedDevices: [] };

function create_camera_setup_prompt() {
	const { subscribe, set, update } = writable<CameraSetupPromptState>({ ...initialState });

	return {
		subscribe,
		/** Opens the prompt for a device and records it for {@link hasPrompted}. */
		open: (deviceId: string) =>
			update((state) => ({
				...state,
				deviceId,
				open: true,
				promptedDevices: [...new Set([...state.promptedDevices, deviceId])],
			})),
		close: () => update((state) => ({ ...state, open: false })),
		/** Marks a device auto-prompted without opening. */
		markPrompted: (deviceId: string) =>
			update((state) => ({
				...state,
				promptedDevices: [...new Set([...state.promptedDevices, deviceId])],
			})),
		/** Whether the device was already auto-prompted this session. */
		hasPrompted: (deviceId: string): boolean =>
			get(camera_setup_prompt).promptedDevices.includes(deviceId),
		/** Clears session state (call on logout). */
		reset: () => set({ ...initialState }),
		/**
		 * Clears one device's prompt memory so it can auto-open again, and closes
		 * the prompt when it is currently open for that device.
		 */
		resetDevice: (deviceId: string) =>
			update((state) => ({
				...state,
				open: state.open && state.deviceId !== deviceId,
				promptedDevices: state.promptedDevices.filter((id) => id !== deviceId),
			})),
	};
}

export const camera_setup_prompt = create_camera_setup_prompt();

/**
 * Setup prompt — for cloud recordings encryption setup. Shows only when the
 * trigger is open for this exact device and the camera is not shared with us.
 *
 * Takes the subscribed value (`$camera_setup_prompt`) so callers stay reactive.
 */
export const shouldShowSetupPrompt = (state: CameraSetupPromptState, device: Device): boolean =>
	state.open && !device?.shared && !!device?.id && state.deviceId === real_id(device.id);
